import React, { useState } from "react";

import { Typography } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";

import { useNotification } from "Containers/Notifications";
import { Button, ButtonWrapper } from "ds/Button";
import Checkbox from "ds/Checkbox";
import Modal from "ds/Modal";
import Select from "ds/Select";
import { ClosableTag } from "ds/Tag";
import { useAdddProjectToTeams } from "Reducers/access/teams/hooks/useAddProjectToTeams";
import { useGetAllProjectTeamAccesses } from "Reducers/access/teams/teams.slice";
import { useAllTeams } from "Reducers/organization/team/teamList.slice";
import {
  OverflowListActions,
  TeamList
} from "src/organization/pages/settings/pages/teams/AddProjectAccessModal.styles";

import type { Organization } from "@packages/client";
import type { Team } from "Api/teams/teams.types";

type AddProjectToTeamModalProps = {
  projectId: string;
  organization: Organization;
  onClose: () => void;
};

export const AddProjectToTeamModal = (props: AddProjectToTeamModalProps) => {
  const intl = useIntl();
  const notify = useNotification();
  const [teams, isLoading] = useAllTeams({
    organizationId: props.organization.id
  });
  const [addProjectToTeams, isAdding] = useAdddProjectToTeams();
  const [selectedTeams, setSelectedTeams] = useState<Array<Team>>([]);
  const [allTeams] = useGetAllProjectTeamAccesses({
    projectId: props.projectId
  });

  const validTeams = teams?.filter(
    team => !allTeams?.find(t => t.id === team.id)
  );

  const options = validTeams
    ?.filter(team => !selectedTeams.find(t => t.id === team.id))
    .map(team => ({ value: team.id, label: team.label }));

  const addToAllTeamsChecked =
    !!validTeams?.length && selectedTeams.length == validTeams?.length;

  const onCheckedChanged = () =>
    !addToAllTeamsChecked && teams
      ? setSelectedTeams(validTeams || [])
      : setSelectedTeams([]);

  const onSelectChange = (e: { value: string; label: string } | null) => {
    const selectedTeam = teams?.find(team => team.id == e?.value);
    if (selectedTeam) setSelectedTeams([...selectedTeams, selectedTeam]);
  };

  const onRemoveTeam = (element: Team) =>
    setSelectedTeams(selectedTeams.filter(team => team.id !== element.id));

  const onClearAll = () => setSelectedTeams([]);

  const onAddToTeam = async () => {
    const response = await addProjectToTeams(
      props.projectId,
      selectedTeams.map(team => ({
        team_id: team.id
      }))
    );
    if (response) {
      if (response.rejected.length) {
        response.rejected.forEach(({ teamId }) => {
          const team = teams?.find(team => team.id === teamId);
          notify.error(`Project could not be added to team ${team?.label}`);
        });
      }
      if (response.fulfilled.length) {
        notify.success(
          intl.formatMessage({ id: "access.team.bulk.fulfilled.title" }),
          intl.formatMessage(
            { id: "access.team.bulk.fulfilled.description" },
            { count: response.fulfilled.length }
          )
        );
        props.onClose();
      }
    }
  };

  return (
    <Modal
      isOpen
      size="medium"
      onOpenChange={props.onClose}
      handleClose={props.onClose}
    >
      <Typography.Title tag="h3">
        <FormattedMessage id="organization.member.bulk.team.invite" />
      </Typography.Title>
      <Checkbox
        forId="allteams"
        label={<FormattedMessage id="organization.member.bulk.team.allTeams" />}
        checked={addToAllTeamsChecked}
        disabled={isLoading}
        onCheckedChanged={onCheckedChanged}
        id="organization.member.bulk.team.allTeams"
      />
      <Select
        id="organization.member.bulk.team.searchTeam"
        isMulti={false}
        isDisabled={isLoading || addToAllTeamsChecked}
        value={null}
        options={options?.length ? options : undefined}
        placeholder={intl.formatMessage({
          id: "organization.member.bulk.team.searchTeam"
        })}
        onChange={onSelectChange}
      />

      {!!selectedTeams.length && (
        <>
          <OverflowListActions>
            <Typography.Label>
              <FormattedMessage
                id="organization.member.bulk.team.selectedTeams"
                values={{ count: selectedTeams.length }}
              />
            </Typography.Label>
            <Button variant="link" onClick={onClearAll} analyticId="clear_all">
              <FormattedMessage id="clear_all" />
            </Button>
          </OverflowListActions>
          <TeamList>
            {selectedTeams
              ?.sort((a, b) => a.label.localeCompare(b.label))
              .map(team => (
                <ClosableTag
                  variant="snow"
                  key={team.id}
                  onClose={() => onRemoveTeam(team)}
                >
                  {team.label}
                </ClosableTag>
              ))}
          </TeamList>
        </>
      )}

      <ButtonWrapper spacing="modal" justifyContent="end">
        <Button variant="secondary" analyticId="cancel" onClick={props.onClose}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          analyticId="organization.member.bulk.team.invite"
          disabled={isLoading || !selectedTeams.length || isAdding}
          onClick={onAddToTeam}
        >
          <FormattedMessage id="organization.member.bulk.team.invite" />
        </Button>
      </ButtonWrapper>
    </Modal>
  );
};
